import { EventSourcePolyfill } from 'event-source-polyfill';
import { useAtomValue } from 'jotai';
import React, { useCallback, useEffect, useRef } from 'react';
import { toast } from 'react-toastify';

import { NewMailToast } from '@/components/toast/NewMail.toast.tsx';
import { CONFIG } from '@/config/config.ts';
import { ToastConfig } from '@/config/ToastConfig.util.ts';
import { useAppConfig } from '@/contexts/appConfig/AppConfig.context.ts';
import { useUserContext } from '@/contexts/userContext/User.context.ts';
import { isDeltaEmailBasicUpdate } from '@/models/BasicEmail.model.ts';
import { RouteEnum } from '@/models/route/Route.model.ts';
import { useAppNavigation } from '@/utils/useAppNavigation.util.ts';
import { useTranslationWithPrefix } from '@/utils/useTranslationWithPrefix.ts';
import { folderStateAtom, useEmailsContext } from '@/views/emails/context/EmailsContext.ts';
import { useAddNewEmail } from '@/views/emails/util/useAddNewEmail.util.ts';

export const SseController: React.FC = () => {
  const { isEmailEnabled } = useAppConfig();
  const { email: userEmail, userId } = useUserContext();
  const { addNewEmail } = useAddNewEmail();
  const { emailsState, updateEmailState } = useEmailsContext();
  const { page } = useAtomValue(folderStateAtom);
  const hasErrorOccurred = useRef(false);
  const { t } = useTranslationWithPrefix('utils.sse');
  const eventSource = useRef<EventSourcePolyfill | undefined>(undefined);
  const { push } = useAppNavigation();

  const handleGoToEmails = () => {
    push(RouteEnum.EMAILS);
  };

  const handleSse = useCallback(
    async (sseData: { detail: unknown }) => {
      const { detail } = sseData;
      console.debug('sseData', sseData);
      if (!isDeltaEmailBasicUpdate(detail)) {
        throw new Error('Błędny komunikat SSE');
      }
      await addNewEmail({
        currentFolderId: emailsState.selectedBox?.folderId,
        currentPage: page,
        newEmail: detail,
      });
      const { email, show } = detail;
      if (email.isDraft.value || !email.from.set || email.from.value?.value === userEmail) {
        return;
      }
      if (show) {
        toast(
          <NewMailToast
            mail={detail}
            onGoToEmails={handleGoToEmails}
            updateEmailState={updateEmailState}
          />,
          {
            ...ToastConfig.default,
            autoClose: 3000,
          },
        );
      }
    },
    [userEmail, emailsState.selectedBox?.folderId, page],
  );

  useEffect(() => {
    if (!isEmailEnabled) {
      return;
    }

    // @ts-expect-error: this is ok
    window.addEventListener('sseEvent', handleSse);

    return () => {
      // @ts-expect-error: this is ok
      window.removeEventListener('sseEvent', handleSse);
    };
  }, [handleSse, isEmailEnabled]);

  useEffect(() => {
    if (!isEmailEnabled) {
      return;
    }

    console.debug('SSE');
    if (eventSource.current) {
      console.debug('SSE already initialized');
      return;
    }
    console.debug('creating new event source');
    const config: { headers: Record<string, string> } = { headers: {} };
    if (CONFIG.ENVIRONMENT === 'local') {
      config.headers['x-auth-token'] = `${CONFIG.JSESSION}`;
    }

    eventSource.current = new EventSourcePolyfill(
      `${CONFIG.HOST}/api/users/${userId}/emails`,
      config,
    );
    eventSource.current.onmessage = event => {
      const data = JSON.parse(event.data);
      if (!Array.isArray(data)) {
        throw new Error('Błędny komunikat SSE');
      }
      if (data.length === 0) {
        return;
      }
      data.forEach(dataItem => {
        window.dispatchEvent(new CustomEvent('sseEvent', { detail: dataItem }));
      });

      if (hasErrorOccurred.current) {
        console.debug('Połączenie zostało wznowione.');
        hasErrorOccurred.current = false;
        // toast(t('toast.connectionReestablished'), ToastConfig.success);
      }
    };

    eventSource.current.onerror = event => {
      console.error('Błąd połączenia SSE');
      console.error({ event });
      if (!hasErrorOccurred.current && eventSource.current?.readyState === EventSource.CLOSED) {
        console.log('Połączenie zostało zamknięte. Próba ponownego połączenia...');
        // toast(t('toast.connectionError'), ToastConfig.error);
      }
      hasErrorOccurred.current = true;
    };

    return () => {
      console.debug('closing event source');
      eventSource.current?.close();
      eventSource.current = undefined;
    };
  }, [isEmailEnabled, t, userId]);

  return null;
};
